// src/components/purchases/DoubleOrderWarningModal.tsx
// Warning modal shown when user tries to add products that are already on order

import React from 'react';
import { X, AlertTriangle, Truck } from 'lucide-react';
import { Product } from '../../services/productCatalogAPI';
import { ProductOnOrderSummary } from '../../services/purchaseLineItemsAPI';

interface DoubleOrderWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed: () => void;
  productsOnOrder: Array<{
    product: Product;
    orderInfo: ProductOnOrderSummary;
  }>;
  totalSelectedCount: number;
}

const DoubleOrderWarningModal: React.FC<DoubleOrderWarningModalProps> = ({
  isOpen,
  onClose,
  onProceed,
  productsOnOrder,
  totalSelectedCount
}) => {
  if (!isOpen) return null;

  const notOnOrderCount = totalSelectedCount - productsOnOrder.length;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-amber-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-full">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Products Already On Order
              </h2>
              <p className="text-sm text-gray-600">
                {productsOnOrder.length} of {totalSelectedCount} selected product{totalSelectedCount !== 1 ? 's' : ''} already ha{productsOnOrder.length === 1 ? 's' : 've'} open orders
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-amber-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          <div className="space-y-3">
            {productsOnOrder.map(({ product, orderInfo }) => (
              <div
                key={product.productId}
                className="border border-amber-200 rounded-lg p-3 bg-amber-50"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{product.name}</p>
                    {product.manufacturer && (
                      <p className="text-sm text-gray-600">{product.manufacturer}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-blue-600 bg-blue-50 px-2 py-1 rounded text-sm">
                    <Truck className="w-3 h-3" />
                    <span>{orderInfo.totalPendingQuantity} on order</span>
                  </div>
                </div>

                {/* Order details */}
                <div className="mt-2 space-y-1">
                  {orderInfo.orders.slice(0, 3).map((order, idx) => (
                    <div key={idx} className="text-xs text-gray-600 flex items-center gap-2">
                      <span className="font-medium">
                        {order.pendingQuantity} pending
                      </span>
                      {order.vendor && (
                        <>
                          <span>•</span>
                          <span>{order.vendor}</span>
                        </>
                      )}
                      {order.poNumber && (
                        <>
                          <span>•</span>
                          <span>{order.poNumber}</span>
                        </>
                      )}
                      {order.purchaseDate && (
                        <>
                          <span>•</span>
                          <span>{new Date(order.purchaseDate).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  ))}
                  {orderInfo.orders.length > 3 && (
                    <p className="text-xs text-gray-500 italic">
                      +{orderInfo.orders.length - 3} more order{orderInfo.orders.length - 3 !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {notOnOrderCount > 0 && (
            <p className="mt-4 text-sm text-gray-600">
              The other {notOnOrderCount} selected product{notOnOrderCount !== 1 ? 's are' : ' is'} not currently on order.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onProceed}
            className="px-4 py-2 text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors"
          >
            Order More Anyway
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoubleOrderWarningModal;
